import { LegalLayout } from "./LegalLayout";

/**
 * Haftungsausschluss-Seite gemäß § 7 ff. TMG / DDG für eine private
 * Lern-App mit KI-generierten Inhalten. Public route at /haftung.
 *
 * Inhaltlich abgedeckt:
 *   - § 1: Haftung für eigene Inhalte (Standard TMG)
 *   - § 2: PPLearning-spezifisch — Vokabeln + Übersetzungen + Beispiele
 *   - § 3: KI-generierte Inhalte (Anthropic-Claude-Klausel)
 *   - § 4: Haftung für externe Links (Standard)
 *   - § 5: Urheberrecht
 *   - § 6: Verbindlichkeit der offiziellen LBA-Sprachprüfung (Aviation)
 *
 * Bewusst Text-only — kein Click-through, kein Modal, das den Lernfluss
 * unterbricht. Reachable in ≤ 2 Klicks per Footer-Link auf jeder Seite.
 */
export function DisclaimerPage() {
  return (
    <LegalLayout title="Haftungsausschluss" subtitle="Stand: Mai 2026">
      <section>
        <h2 className="text-base font-semibold text-fg mb-1">1. Haftung für Inhalte</h2>
        <p>
          Die Inhalte dieser App wurden mit größtmöglicher Sorgfalt erstellt. Als
          Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG (Telemediengesetz) bzw.
          § 7 Abs. 1 DDG (Digitale-Dienste-Gesetz) für eigene Inhalte auf dieser
          Plattform nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10
          TMG bzw. §§ 8 bis 10 DDG sind wir als Diensteanbieter jedoch nicht
          verpflichtet, übermittelte oder gespeicherte fremde Informationen zu
          überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige
          Tätigkeit hinweisen.
        </p>
        <p>
          Verpflichtungen zur Entfernung oder Sperrung der Nutzung von
          Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt.
          Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der
          Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden
          von entsprechenden Rechtsverletzungen werden wir diese Inhalte
          umgehend entfernen.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">
          2. Vokabeln, Übersetzungen und Beispielsätze
        </h2>
        <p>
          PPLearning ist eine <strong>private Lernhilfe</strong> zur Vorbereitung
          auf die englische Sprachprüfung im Rahmen der Privatpilotenlizenz
          (PPL). Die enthaltenen Vokabeln, deren deutsche Übersetzungen sowie
          die Beispielsätze wurden mit größter Sorgfalt zusammengestellt,
          insbesondere unter Heranziehung anerkannter Quellen aus dem
          Aviation-Englisch-Bereich.
        </p>
        <p>
          Trotz dieser Sorgfalt können sich <strong>Fehler, veraltete Begriffe,
          unpräzise Übersetzungen oder Tippfehler</strong> einschleichen. Eine
          Gewähr für die Vollständigkeit, Aktualität, Richtigkeit oder
          Eignung der dargestellten Inhalte für einen bestimmten Zweck wird
          ausdrücklich nicht übernommen. Eine Haftung für etwaige Schäden,
          die durch die Nutzung dieser Inhalte entstehen — insbesondere für
          ein Nichtbestehen oder fehlerhaftes Ergebnis einer Sprachprüfung —
          ist ausgeschlossen, soweit nicht Vorsatz oder grobe Fahrlässigkeit
          vorliegt.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">3. KI-generierte Inhalte</h2>
        <p>
          Ein Teil der Beispielsätze zu einzelnen Vokabeln wird automatisiert
          unter Verwendung von <strong>Large-Language-Modellen</strong>
          (insbesondere <em>Claude</em> der Anthropic PBC) generiert. KI-Modelle
          können <strong>fehlerhafte, ungenaue oder im Aviation-Kontext
          unpassende</strong> Inhalte liefern („Halluzinationen"). Wir
          überprüfen generierte Inhalte stichprobenartig, übernehmen aber
          keine Gewähr für deren fachliche Richtigkeit.
        </p>
        <p>
          Hinweise auf konkrete Fehler nehmen wir per E-Mail an{" "}
          <span className="font-mono">contact[at]concept4it.com</span> entgegen
          und korrigieren sie zeitnah.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">4. Haftung für Links</h2>
        <p>
          Unser Angebot enthält punktuell Links zu externen Webseiten Dritter
          (z. B. zur Europäischen Online-Streitbeilegungsplattform oder zur
          Aufsichtsbehörde). Auf deren Inhalte haben wir keinen Einfluss. Für
          die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter
          oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten
          wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße
          überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung
          nicht erkennbar.
        </p>
        <p>
          Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist
          jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht
          zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir
          derartige Links umgehend entfernen.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">5. Urheberrecht</h2>
        <p>
          Die durch die Seitenbetreiber erstellten Inhalte und Werke auf
          diesen Seiten unterliegen dem deutschen Urheberrecht. Die
          Vervielfältigung, Bearbeitung, Verbreitung und jede Art der
          Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der
          schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
          Downloads und Kopien dieser Seite sind nur für den privaten,
          nicht kommerziellen Gebrauch gestattet.
        </p>
        <p>
          Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt
          wurden, werden die Urheberrechte Dritter beachtet. Insbesondere
          werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie
          trotzdem auf eine Urheberrechtsverletzung aufmerksam werden,
          bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden
          von Rechtsverletzungen werden wir derartige Inhalte umgehend
          entfernen.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">
          6. Verbindlichkeit der offiziellen Sprachprüfung
        </h2>
        <p>
          PPLearning ist <strong>keine offizielle Vorbereitungsliteratur</strong>,
          kein zertifiziertes Sprachtraining und keine Flugschule. Allein
          verbindlich für den Erwerb der englischen Sprachkompetenz im Rahmen
          der Privatpilotenlizenz sind:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            die offiziellen Prüfungsanforderungen des{" "}
            <strong>Luftfahrt-Bundesamtes (LBA)</strong> bzw. der zuständigen
            europäischen Stelle (EASA),
          </li>
          <li>die offiziellen Lehrmaterialien anerkannter Flugschulen, und</li>
          <li>
            die durch anerkannte Prüfer abgenommene{" "}
            <strong>Language Proficiency Check (LPC)</strong>-Prüfung.
          </li>
        </ul>
        <p className="mt-3">
          PPLearning dient lediglich als ergänzendes Übungswerkzeug und ersetzt
          weder die offizielle Prüfungsvorbereitung noch die Prüfung selbst.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">7. Änderungen</h2>
        <p>
          Wir behalten uns vor, diesen Haftungsausschluss anzupassen, sofern
          sich rechtliche Anforderungen, die eingesetzten technischen Mittel
          (z. B. KI-Anbieter) oder die App selbst ändern. Das Datum oben gibt
          den jeweils aktuellen Stand an.
        </p>
      </section>
    </LegalLayout>
  );
}
